import React from "react";
import { Box, Chip, type SxProps, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";

interface ActiveFiltersProps<T extends Record<string, any>> {
  appliedFilters: T;
  onClearFilter: (filterId: keyof T) => void;
  displayNameMap?: Partial<Record<keyof T, string>>;
  valueFormatter?: (key: keyof T, value: any) => string;
  sx?: SxProps;
}

export const ActiveFilters = <T extends Record<string, any>>({
  appliedFilters,
  onClearFilter,
  displayNameMap = {},
  valueFormatter,
  sx,
}: ActiveFiltersProps<T>) => {
  const activeFilters = Object.entries(appliedFilters)
    .filter(([, value]) => value !== null && value !== "")
    .map(([key, value]) => ({ key, value }));

  if (activeFilters.length === 0) return null;

  const getDisplayName = (key: keyof T) =>
    displayNameMap[key] || (key as string);

  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
        background: "#F9FBFC",
        padding: "12px",
        borderRadius: "8px",
        ...sx,
      }}
    >
      <Typography
        variant="body2"
        sx={{ mr: 1, color: "text.secondary", fontWeight: "bold" }}
      >
        Active Filters:
      </Typography>

      {activeFilters.map(({ key, value }) => (
        <Chip
          key={key}
          label={`${getDisplayName(key as keyof T)}: ${valueFormatter ? valueFormatter(key as keyof T, value) : value}`}
          onDelete={() => onClearFilter(key as keyof T)}
          deleteIcon={<Close />}
          size="small"
          variant="outlined"
          sx={{
            "& .MuiChip-deleteIcon": {
              fontSize: "16px",
            },
          }}
        />
      ))}
    </Box>
  );
};
